'use client';

import { useState } from 'react';
import { FadeUpSection } from '@/components/fade-up-section';
import PageShell from '@/components/page-shell';
import { useContent } from '@/hooks/use-content';

const PROFILE_IMAGE = '/images/owner-portrait.webp';

export default function ContactPage() {
  const c = useContent();
  const p = c.contactPage;
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(data.error || p.formErrorText);
      setSuccess(p.formSuccessText);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : p.formErrorText);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <main>
        <FadeUpSection className="px-6 md:px-8 py-12 md:py-20">
          <div className="bm-container text-start">
            <h1 className="text-4xl md:text-6xl font-black mb-6">{p.pageTitle}</h1>
            <p className="text-lg md:text-xl text-[#5e5e5e] max-w-2xl">{p.pageDescription}</p>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-12">
              <div className="lg:col-span-7 bg-[var(--bm-fog)] p-8 md:p-10 border border-[var(--bm-light)]">
                <form className="space-y-8 text-start" onSubmit={handleSubmit}>
                  {error ? <p className="text-sm text-red-600">{error}</p> : null}
                  {success ? <p className="text-sm text-green-700 font-semibold">{success}</p> : null}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[var(--bm-mid)]">{p.formNameLabel}</label>
                      <input
                        className="w-full bg-transparent border-b border-[var(--bm-light)] focus:border-[var(--bm-dark)] px-0 py-2 outline-none"
                        placeholder={p.formNameLabel}
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-[var(--bm-mid)]">{p.formEmailLabel}</label>
                      <input
                        className="w-full bg-transparent border-b border-[var(--bm-light)] focus:border-[var(--bm-dark)] px-0 py-2 outline-none"
                        placeholder={p.formEmailLabel}
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-[var(--bm-mid)]">{p.formSubjectLabel}</label>
                    <input
                      className="w-full bg-transparent border-b border-[var(--bm-light)] focus:border-[var(--bm-dark)] px-0 py-2 outline-none"
                      placeholder={p.formSubjectLabel}
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-[var(--bm-mid)]">{p.formMessageLabel}</label>
                    <textarea
                      className="w-full bg-transparent border-b border-[var(--bm-light)] focus:border-[var(--bm-dark)] px-0 py-2 outline-none resize-none"
                      placeholder={p.formMessageLabel}
                      rows={5}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                    />
                  </div>
                  <button
                    className="px-10 py-4 bm-btn text-sm font-bold hover:opacity-90 transition-colors disabled:opacity-60"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? p.formSubmittingText : p.formSubmitText}
                  </button>
                </form>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-6">
                <div className="bm-portrait-frame aspect-[3/4] max-h-[420px]">
                  <img alt={c.brand} className="bm-portrait-img" src={PROFILE_IMAGE} />
                </div>
                <p className="text-xs font-bold text-[var(--bm-dark)]">{p.profileBadge}</p>
                <div className="space-y-6 p-2">
                  <div>
                    <span className="text-xs font-bold text-[var(--bm-dark)]">{p.emailLabel}</span>
                    <a className="block text-2xl font-bold hover:underline mt-1" href={`mailto:${p.emailValue}`}>
                      {p.emailValue}
                    </a>
                  </div>
                  {p.phoneValue ? (
                    <div>
                      <span className="text-xs font-bold text-[var(--bm-dark)]">{p.phoneLabel}</span>
                      <a className="block text-2xl font-bold hover:underline mt-1" href={`tel:${p.phoneValue}`}>
                        {p.phoneValue}
                      </a>
                    </div>
                  ) : null}
                  <div>
                    <span className="text-xs font-bold text-[var(--bm-dark)]">{p.locationLabel}</span>
                    <p className="text-xl font-bold mt-1">{p.locationValue}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FadeUpSection>

        <FadeUpSection className="px-6 md:px-8 py-12 border-t border-[var(--bm-light)]">
          <div className="bm-container text-start">
            <span className="text-xs font-bold text-[var(--bm-dark)] block mb-3">{p.baseOperationsLabel}</span>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4">{p.baseCity}</h2>
            <p className="text-lg text-[#5e5e5e] max-w-xl">{p.baseDescription}</p>
          </div>
        </FadeUpSection>
      </main>
    </PageShell>
  );
}
